import React from 'react'
import { Button, Tooltip, Flex } from '@chakra-ui/react'
import { FaPlus, FaMinus } from 'react-icons/fa6'

import {
    ClientFieldsFragment,
    namedOperations,
    useCustomerSegmentQuery,
    useUpdateCustomerSegmentMutation,
} from '~graphql/generated/graphql'
import { useClinic } from '~contexts/clinic-context'
import { useToastFeedback } from '~utils/hooks/use-toast-feedback'

interface Props {
    client: ClientFieldsFragment
}

export default function WaitlistButton({ client }: Props) {
    const { clinic } = useClinic()
    const waitlistSegment = clinic?.settings?.waitlist_customer_segment || null

    const addToasts = useToastFeedback(
        'Client added to waitlist',
        'Error adding client to waitlist',
    )

    const removeToasts = useToastFeedback(
        'Client removed from waitlist',
        'Error removing client from waitlist',
    )

    const { data: segmentData, loading: segmentLoading } =
        useCustomerSegmentQuery({
            variables: { id: waitlistSegment?.id || '' },
            skip: !waitlistSegment?.id,
        })

    const segment = segmentData?.customerSegment

    const isAlreadyOnWaitlist =
        segment?.included_clients?.includes(client.id) ?? false

    const [updateSegment, { loading: updatingSegment }] =
        useUpdateCustomerSegmentMutation({
            onCompleted: () => {
                if (isAlreadyOnWaitlist) {
                    removeToasts.onCompleted()
                } else {
                    addToasts.onCompleted()
                }
            },
            onError: () => {
                if (isAlreadyOnWaitlist) {
                    removeToasts.onError()
                } else {
                    addToasts.onError()
                }
            },
        })

    const handleToggleWaitlist = () => {
        if (!segment) return

        const includedClients = segment.included_clients || []
        const excludedClients = segment.excluded_clients || []

        if (isAlreadyOnWaitlist) {
            // Remove from waitlist
            const newIncluded = includedClients.filter(c => c !== client.id)
            const newExcluded =
                newIncluded.length === includedClients.length
                    ? [...excludedClients, client.id]
                    : excludedClients

            updateSegment({
                variables: {
                    id: segment.id,
                    data: {
                        included_clients: newIncluded,
                        excluded_clients: newExcluded,
                    },
                },
                refetchQueries: [
                    namedOperations.Query.getClientsByCustomerSegmentRPC,
                    namedOperations.Query.customerSegment,
                ],
                awaitRefetchQueries: true,
            })
        } else {
            // Add to waitlist
            const newIncluded = [...new Set([...includedClients, client.id])]
            const newExcluded = excludedClients.filter(c => c !== client.id)

            updateSegment({
                variables: {
                    id: segment.id,
                    data: {
                        included_clients: newIncluded,
                        excluded_clients: newExcluded,
                    },
                },
                refetchQueries: [
                    namedOperations.Query.getClientsByCustomerSegmentRPC,
                    namedOperations.Query.customerSegment,
                ],
                awaitRefetchQueries: true,
            })
        }
    }

    return (
        <Flex justifyContent="flex-end" mb={2}>
            <Tooltip
                label={
                    !waitlistSegment
                        ? 'No waitlist segment selected. This can be configured in the Daily Huddle.'
                        : isAlreadyOnWaitlist
                          ? `Remove client from ${waitlistSegment.name}`
                          : `Add client to ${waitlistSegment.name}`
                }
                placement="top"
                hasArrow
            >
                <Button
                    size="xs"
                    variant="outline"
                    colorScheme={isAlreadyOnWaitlist ? 'red' : 'gray'}
                    leftIcon={isAlreadyOnWaitlist ? <FaMinus /> : <FaPlus />}
                    onClick={handleToggleWaitlist}
                    isDisabled={!waitlistSegment || updatingSegment}
                    isLoading={updatingSegment || segmentLoading}
                >
                    {isAlreadyOnWaitlist
                        ? 'Remove from waitlist'
                        : 'Add to waitlist'}
                </Button>
            </Tooltip>
        </Flex>
    )
}
